import { ConnectionType, type ConnectionData } from './enums.interface';

/**
 * Lightweight summary of a linked ObjectStorage connection returned alongside DataFusion connections.
 */
export interface StorageSummary {
  connectionId: number;
  provider: string;
  bucket: string;
  prefix?: string | null;
}

/**
 * Represents a connection to an external service or database.
 * When returned from GET /connection or GET /connection/{id}, the server wraps this
 * in a ConnectionResponse that flattens these fields and adds an optional linkedStorage.
 */
export interface Connection {
  connectionId?: number;
  projectId: number;
  name: string;
  description?: string | null;
  type: ConnectionType;
  config: ConnectionData;
  createdAt?: string;
  updatedAt?: string;
  /** Only present for DataFusion connections that have a linked ObjectStorage connection. */
  linkedStorage?: StorageSummary | null;
}

/**
 * Supported database engine types.
 */
export enum DatabaseType {
  PostgreSQL = 'PostgreSQL',
  SQLServer = 'SQLServer'
}

/**
 * Database connection config.
 */
export interface DatabaseConfig {
  type?: ConnectionType;
  databaseType?: DatabaseType;
  host: string;
  port: string;
  username: string;
  dbname: string;
  options?: string | null;
  passwordSecretId?: number | null;
}

/**
 * DataFusion connection config (compute-only).
 * Storage credentials are managed via linked ObjectStorage connections.
 */
export interface DataFusionConfig {
  type?: ConnectionType;
  url: string;
  cacheKey?: string;
}

/**
 * LLM connection config
 */
export interface LLMConfig {
  type?: ConnectionType;
  llmType: LLMType;
  apiKeySecretId: number;
  endpoint?: string | null;
}

/**
 * Connection config for Box.com
 */
export type BoxOperation = 'list' | 'download' | 'upload' | 'uploadVersion' | 'delete' | 'move' | 'mkdir' | 'comment';

export interface BoxConfig {
  type?: ConnectionType;
  authType: 'jwt' | 'ccg';
  /** Registry model (SCRUM-614): the platform-managed Box app this connection uses. */
  boxAppId?: number;
  /** Hard floor for downscoping: the connection can never touch outside this Box folder subtree. */
  rootFolderId?: string;
  // Legacy inline credentials — deprecated; auto-migrated onto the registry server-side.
  apiKeySecretId?: number;
  clientId?: string;
  clientSecretId?: number;
  enterpriseId?: string;
  allowedOperations?: BoxOperation[];
}

/** A platform-managed Box app as offered in the connection picker (no secrets). */
export interface BoxAppOption {
  boxAppId: number;
  name: string;
  description: string;
}

/**
 * Authentication methods for custom Service connections.
 */
export type ServiceAuthType = 'bearer' | 'customHeader' | 'queryParam';

export interface ServiceAuth {
  type: ServiceAuthType;
  secretId: number;
  headerName?: string;
  paramName?: string;
}

/**
 * Service connection config: references a global service or defines a custom external service.
 */
export interface ServiceConfig {
  type?: ConnectionType;
  /** Reference to a global service. If set, URL comes from the global service record. */
  globalServiceId?: number | null;
  /** Custom URL (required when globalServiceId is null). */
  url?: string | null;
  /** Override timeout in ms. */
  timeoutMs?: number | null;
  /** Auth configuration (only for custom services, not global services). */
  auth?: ServiceAuth | null;
}

/**
 * Parameters for updating a connection.
 */
export interface UpdateConnectionParams {
  connectionId: number;
  name?: string | null;
  description?: string | null;
  type?: ConnectionType | null;
  config?: ConnectionData | null;
}

/**
 * Request for creating a connection.
 * The server accepts a full Connection object, so projectId is required.
 * The config must include a "type" discriminator matching the ConnectionType
 * (e.g. { type: "LLM", llmType: "OpenAI", apiKeySecretId: 1 }).
 */
export interface CreateConnectionRequest {
  projectId: number;
  name: string;
  /** Always send a string (use `''` when empty) so the API receives a non-null description. */
  description: string;
  type: ConnectionType;
  config: ConnectionData;
}

/**
 * Request for updating a connection.
 * Matches server's UpdateConnectionParams struct.
 * The "type" field in config must be the ConnectionData discriminator tag.
 */
export interface UpdateConnectionRequest {
  connectionId: number;
  name?: string;
  description?: string;
  type?: ConnectionType;
  config?: ConnectionData;
}

/**
 * Result of a connection test
 */
export interface ConnectionTestResult {
  success: boolean;
  message?: string;
  filesCount?: number;
}

/**
 * The types of LLMs that can be used.
 */
export enum LLMType {
  OpenAI = 'OpenAI',
  AzureOpenAI = 'AzureOpenAI',
  Gemini = 'Gemini',
  Claude = 'Claude'
}

// Type guards for specific config types
export function isBoxConfig(config: any): config is BoxConfig {
  return 'authType' in config &&
    !('host' in config) && !('accessKey' in config) && !('llmType' in config);
}

export function isBoxJwtConfig(config: any): config is BoxConfig {
  return isBoxConfig(config) && config.authType === 'jwt' && 'apiKeySecretId' in config;
}

export function isBoxCcgConfig(config: any): config is BoxConfig {
  return isBoxConfig(config) && config.authType === 'ccg' && 'clientId' in config && 'clientSecretId' in config;
}

/** Registry-backed Box connection (references a platform-managed Box app). */
export function isBoxRegistryConfig(config: any): config is BoxConfig {
  return isBoxConfig(config) && config.boxAppId != null;
}

/** Legacy Box connection still carrying inline credentials (pre-registry). */
export function isLegacyBoxConfig(config: any): config is BoxConfig {
  return isBoxConfig(config) && config.boxAppId == null;
}

export function isDatabaseConfig(config: any): config is DatabaseConfig {
  return 'host' in config && 'port' in config && 'dbname' in config && 'username' in config;
}

export function isDataFusionConfig(config: any): config is DataFusionConfig {
  return 'url' in config && !('host' in config) && !('llmType' in config) && !('authType' in config) && !('provider' in config);
}

export function isLLMConfig(config: any): config is LLMConfig {
  return 'llmType' in config && 'apiKeySecretId' in config;
}

export function isServiceConfig(config: any): config is ServiceConfig {
  return ('globalServiceId' in config) || ('url' in config && !('host' in config) && !('llmType' in config) && !('cacheKey' in config));
}
